import { useTranslation } from 'react-i18next';
import { Box, CircularProgress, List, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useMutation, useQuery } from '@tanstack/react-query';
import { getMessageFromError, DetailView, ApiError } from '@pro4-erp/core-web';
import { FileItem, SqoFile } from '@/common';
import { getArchiveDocuments } from '../api/getArchiveDocuments';
import { checkIfErrorIsAccessAuthorizationError } from '@/core/utils/getMessageFromError';
import { downloadFileFromDws } from '../api/downloadFileFromDs';
import VisitHistory from './VisitHistory';

type AnswersProps = {
  visitId: string;
  docType: 'changes' | 'progVersion' | 'role' | 'questions' | 'cross' | 'ncr' | 'plan' | 'ppor';
};

const ArchiveFiles = ({ visitId, docType }: AnswersProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();

  const downloadFileMutation = useMutation({
    mutationFn: downloadFileFromDws,
    onSuccess: async (result) => {
      const { res } = result;
      const blob = await res.blob();
      // Create blob link to download
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', result.filename);

      // Append to html link element page
      document.body.appendChild(link);

      // Start download
      link.click();

      enqueueSnackbar(
        t('Successfully downloaded file.', { defaultValue: 'Successfully downloaded file.' }),
        { variant: 'success' }
      );
    },
    onError: (error: Error) => {
      if (checkIfErrorIsAccessAuthorizationError(error)) {
        enqueueSnackbar(
          t('No access authorisation!', { defaultValue: 'No access authorisation!' }),
          { variant: 'error', anchorOrigin: { vertical: 'top', horizontal: 'center' } }
        );
      } else {
        enqueueSnackbar(getMessageFromError(error), { variant: 'error' });
      }
    },
  });

  const query = useQuery<SqoFile[], ApiError>(
    ['archive-doc', docType, visitId],
    () => getArchiveDocuments(visitId, docType),
    {
      refetchOnWindowFocus: false,
    }
  );
  return (
    <Box>
      {query.isLoading && (
        <Box
          sx={{
            width: '90vw',
            height: '50vh',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <CircularProgress size={100} />
        </Box>
      )}
      {!query.isLoading && query.data && query.data.length > 0 && (
        <List>
          {query.data.map((item) => (
            <FileItem
              key={item.id}
              name={item.title || ''}
              uploadedBy={item.uploadedBy}
              uploadedAt={item.uploadedAt}
              fileName={item.fileName}
              info={item.info}
              onDownload={() => {
                if (item.dwsDok)
                  downloadFileMutation.mutate({
                    dok: item.dwsDok,
                    filename: item.title || item.dwsDok.originalfilename,
                  });
              }}
            />
          ))}
        </List>
      )}{' '}
      {!query.isLoading && query.data && query.data.length === 0 && (
        <Typography variant="subtitle1">{t('No files', { defaultValue: 'No files' })}</Typography>
      )}
    </Box>
  );
};
const AuditorFilesArchive = ({ visitId, woUid }: { visitId: string; woUid: string }) => {
  const { t } = useTranslation();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <DetailView
        childrenSx={{ padding: '2rem' }}
        tabs={[
          { id: 'ppor', title: t('POR', { defaultValue: 'POR' }) },
          {
            id: 'plan',
            title: t('Plan', { defaultValue: 'Plan' }),
          },
          { id: 'ncr', title: t('NCR', { defaultValue: 'NCR' }) },
          { id: 'cross', title: t('Križanke', { defaultValue: 'Križanke' }) },
          { id: 'questions', title: t('Povpraševanje', { defaultValue: 'Povpraševanje' }) },
          { id: 'role', title: t('Vloge', { defaultValue: 'Vloge' }) },
          {
            id: 'progVersion',
            title: t('Program in vzorčenje', { defaultValue: 'Program in vzorčenje' }),
          },
          { id: 'changes', title: t('Spremembe', { defaultValue: 'Spremembe' }) },
          {
            id: 'history',
            title: t('Dokumentacija obiska', { defaultValue: 'Dokumentacija obiska' }),
          },
        ]}
        defaultTab="ppor"
      >
        {({ TabContent }) => (
          <>
            <TabContent tab="ppor">
              <ArchiveFiles docType="ppor" visitId={visitId} />
            </TabContent>
            <TabContent tab="plan">
              <ArchiveFiles docType="plan" visitId={visitId} />
            </TabContent>
            <TabContent tab="ncr">
              <ArchiveFiles docType="ncr" visitId={visitId} />
            </TabContent>
            <TabContent tab="cross">
              <ArchiveFiles docType="cross" visitId={visitId} />
            </TabContent>
            <TabContent tab="questions">
              <ArchiveFiles docType="questions" visitId={visitId} />
            </TabContent>
            <TabContent tab="role">
              <ArchiveFiles docType="role" visitId={visitId} />
            </TabContent>
            <TabContent tab="progVersion">
              <ArchiveFiles docType="progVersion" visitId={visitId} />
            </TabContent>
            <TabContent tab="changes">
              <ArchiveFiles docType="changes" visitId={visitId} />
            </TabContent>
            <TabContent tab="history">
              <VisitHistory visitId={visitId} woUid={woUid} />
            </TabContent>
          </>
        )}
      </DetailView>
    </Box>
  );
};

export default AuditorFilesArchive;
